import { useRouteState } from '../routeState'
import { useState } from 'react'
import { Download } from 'lucide-react'
import { WorkspaceDialog } from './WorkspaceDialog'
import { downloadTextFile, runtimeStory, scriptCsv } from '../productionExport'
import { projectIssues, slugify } from '../model'
import type { StoryProject } from '../types'

export function ProductionExport({
  project,
  selectedNodeId,
  onClose,
  onReview,
}: {
  project: StoryProject
  selectedNodeId: string
  onClose: () => void
  onReview: () => void
}) {
  const [format, setFormat] = useRouteState<'script' | 'dialogue' | 'runtime'>('export-format', 'dialogue', ['script', 'dialogue', 'runtime'])
  const [scope, setScope] = useRouteState<string>('export-scope', 'all')
  const [speaker, setSpeaker] = useState('')
  const [notice, setNotice] = useState('')
  const chapters = Array.from(
    new Set(
      project.nodes
        .map((node) => node.chapter)
        .filter((chapter): chapter is string => Boolean(chapter)),
    ),
  )
  const speakers = Array.from(
    new Set(
      project.nodes
        .flatMap((node) =>
          node.blocks
            .filter((block) => block.kind === 'dialogue')
            .map((block) => block.speaker?.trim() ?? ''),
        )
        .filter(Boolean),
    ),
  ).sort()
  const nodes = project.nodes.filter(
    (node) =>
      scope === 'all' ||
      (scope === 'selected'
        ? node.id === selectedNodeId
        : node.chapter === scope.slice(8)),
  )
  const lines = nodes.flatMap((node) => [
    ...node.blocks
      .filter(
        (block) =>
          format === 'script' ||
          (block.kind === 'dialogue' &&
            (!speaker ||
              block.speaker?.trim().toLocaleLowerCase() ===
                speaker.toLocaleLowerCase())),
      )
      .map((block) => ({
        id: block.id,
        scene: node.title,
        speaker: block.speaker ?? 'Action',
        text: block.text,
      })),
    ...(format === 'script' && node.kind !== 'ending'
      ? node.choices.map((choice) => ({
          id: choice.id,
          scene: node.title,
          speaker: 'Player choice',
          text: choice.label,
        }))
      : []),
  ])
  const issues = projectIssues(project).filter((issue) => issue.kind === 'flow')
  return (
    <WorkspaceDialog
      title="Export for production"
      eyebrow="FROM THE WRITING ROOM TO THE GAME"
      onClose={onClose}
      className="production-export-dialog"
    >
      <div className="production-export-body">
        <label className="dialog-field">
          Format
          <select
            aria-label="Production export format"
            value={format}
            onChange={(event) => {
              setFormat(event.target.value as typeof format)
              setNotice('')
            }}
          >
            <option value="dialogue">Dialogue & voice-over sheet · CSV</option>
            <option value="script">
              Complete script & localization sheet · CSV
            </option>
            <option value="runtime">Playable story data · JSON</option>
          </select>
        </label>
        {format === 'runtime' ? (
          <div className="runtime-export-info">
            <h3>A small, explicit runtime contract</h3>
            <p>
              Includes scenes, stable line IDs, destinations, variable starting
              values, conditions, and consequences. The format is BRAMBLE
              Runtime v1; your game’s importer can read it without a scripting
              language.
            </p>
            <p>
              {project.nodes.length} scenes · {project.variables?.length ?? 0}{' '}
              variables. Writer notes, character profiles, and planning are kept
              in your project backup.
            </p>
            {issues.length > 0 && (
              <p className="export-review-note">
                There are {issues.length} story flow notes in this draft.{' '}
                <button type="button" onClick={onReview}>
                  Open Review
                </button>
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="production-filters">
              <label className="dialog-field">
                Scenes
                <select
                  aria-label="Scenes to export"
                  value={scope}
                  onChange={(event) => setScope(event.target.value)}
                >
                  <option value="all">Whole story</option>
                  <option value="selected">Current scene</option>
                  {chapters.map((chapter) => (
                    <option key={chapter} value={'chapter:' + chapter}>
                      {chapter}
                    </option>
                  ))}
                </select>
              </label>
              {format === 'dialogue' && (
                <label className="dialog-field">
                  Character
                  <select
                    aria-label="Character to export"
                    value={speaker}
                    onChange={(event) => setSpeaker(event.target.value)}
                  >
                    <option value="">All characters</option>
                    {speakers.map((name) => (
                      <option key={name}>{name}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <p className="export-explanation">
              {lines.length} {lines.length === 1 ? 'line' : 'lines'} ready.
              Includes line IDs, localization keys, scene, speaker, direction,
              word count, and a blank translation column. CSV cells that could
              be spreadsheet formulas are prefixed with an apostrophe.
            </p>
            {lines.length ? (
              <div className="export-preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>Scene / character</th>
                      <th>Text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.slice(0, 6).map((line) => (
                      <tr key={line.id}>
                        <td>
                          {line.scene}
                          <small>{line.speaker}</small>
                        </td>
                        <td>{line.text || '(unwritten)'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {lines.length > 6 && (
                  <p>Previewing the first 6 of {lines.length} lines.</p>
                )}
              </div>
            ) : (
              <div className="export-empty">
                No lines match these filters. Choose another scene or character,
                or add dialogue to your draft.
              </div>
            )}
          </>
        )}
        {notice && (
          <p role="status" className="dialog-message">
            {notice}
          </p>
        )}
      </div>
      <footer className="workspace-dialog-actions">
        <button type="button" className="button" onClick={onClose}>
          Back to writing
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={format !== 'runtime' && !lines.length}
          onClick={() => {
            const filename = slugify(project.title, 'story')
            if (format === 'runtime')
              downloadTextFile(
                JSON.stringify(runtimeStory(project), null, 2) + '\n',
                filename + '-runtime.json',
                'application/json;charset=utf-8',
              )
            else
              downloadTextFile(
                scriptCsv(project, {
                  nodeIds: nodes.map((node) => node.id),
                  dialogueOnly: format === 'dialogue',
                  speaker: format === 'dialogue' ? speaker : undefined,
                }),
                filename +
                  (format === 'dialogue' ? '-dialogue' : '-script') +
                  '.csv',
                'text/csv;charset=utf-8',
              )
            setNotice(
              'Export prepared. Your download includes the current draft.',
            )
          }}
        >
          <Download size={15} /> Download{' '}
          {format === 'runtime' ? 'JSON' : 'CSV'}
        </button>
      </footer>
    </WorkspaceDialog>
  )
}
